import { Injectable } from '@angular/core';
import { BehaviorSubject, from, lastValueFrom, Observable } from 'rxjs';

import {
  RbacService,
  UserPermissionViewModel,
  UserProfileViewModel,
} from '../../common-api/public-api';
import {
  AUTO_LOG_CATEGORY_FOR_LIB,
  AutoLog,
  CommonUtils,
  LocalStorageService,
} from '../../common/public-api';
import { LocalStorageEnum } from '../../rt-shared/enums';
import { AuditInfoService } from '../../rt-shared/services';
import { isNullOrEmpty } from '../utils';

const USER_INFO_STORAGE_KEY = LocalStorageEnum.USER_INFO_STORAGE_KEY;
const USER_PERMISSIONS_STORAGE_KEY =
  LocalStorageEnum.USER_PERMISSIONS_STORAGE_KEY;

/**
 * User information service
 *
 * @export
 * @class UserInfoService
 */
@Injectable({
  providedIn: 'root',
})
export class UserInfoService {
  /**
   * Subject for user data.
   */
  private _user$ = new BehaviorSubject<UserProfileViewModel | null>(null);

  /**
   * Subjet for user permissions data.
   */
  private _permissions$ = new BehaviorSubject<UserPermissionViewModel | null>(
    null
  );

  constructor(
    private _localStorageService: LocalStorageService,
    private _rbacApi: RbacService,
    private _auditInfoService: AuditInfoService
  ) {
    _localStorageService.change$.subscribe({
      next: (event) => {
        this._setupUserInfoFromStorage();
        this._setupPermissionsFromStorage();
      },
    });

    this._setupUserInfoFromStorage();
    this._setupPermissionsFromStorage();
  }

  /**
   * User data subject as observable type.
   */
  get user$(): Observable<UserProfileViewModel | null> {
    return this._user$;
  }

  /**
   * User permissions data subject as observable type.
   */
  get permissions$(): Observable<UserPermissionViewModel | null> {
    return this._permissions$;
  }

  /**
   * Current user data.
   */
  get currentUser(): UserProfileViewModel | null {
    return this._user$.value;
  }

  /**
   * Current user permissions data.
   */
  get currentPermissions(): UserPermissionViewModel | null {
    return this._permissions$.value;
  }

  /**
   * Fetch user related data and update to app data storage.
   *
   * @param {string} mail
   * @return {*}  {Observable<boolean>}
   * @memberof UserInfoService
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  updateUserInfo(mailOrUserNo: string): Observable<boolean> {
    const promise$ = new Promise<boolean>(async (resolve, reject) => {
      try {
        const tmpUser = await lastValueFrom(
          this._rbacApi.rbacUserProfileUserNoGet(mailOrUserNo)
        );
        if (isNullOrEmpty(tmpUser)) {
          throw new Error();
        }
        const tmpUserNo = tmpUser.userNo as string;
        const tmpPermissions = await lastValueFrom(
          this._rbacApi.rbacPermissionUserUserNoGet(tmpUserNo)
        );

        this._localStorageService.setItem(USER_INFO_STORAGE_KEY, tmpUser);
        this._localStorageService.setItem(
          USER_PERMISSIONS_STORAGE_KEY,
          tmpPermissions
        );
        this._user$.next(tmpUser);
        this._permissions$.next(tmpPermissions);
        this._auditInfoService.updateAuditUserInfo();
        resolve(true);
      } catch (err) {
        reject(err);
      }
    });

    return from(promise$);
  }

  /**
   * Clean user related data in app data storage.
   *
   * @memberof UserInfoService
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  clean(): void {
    this._localStorageService.removeItem(USER_INFO_STORAGE_KEY);
    this._localStorageService.removeItem(USER_PERMISSIONS_STORAGE_KEY);
  }

  /**
   * Fetch user data from app data storage and emit user data changed notifications.
   *
   * @private
   * @memberof UserInfoService
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  private _setupUserInfoFromStorage(): void {
    const userData = this._localStorageService.getItem<UserProfileViewModel>(
      USER_INFO_STORAGE_KEY
    );
    if (CommonUtils.toJSON(this.currentUser) !== CommonUtils.toJSON(userData)) {
      this._user$.next(userData);
    }
  }

  /**
   * Fetch user permissions data from app data storage and emit user data changed notifications.
   *
   * @private
   * @memberof UserInfoService
   */
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  private _setupPermissionsFromStorage(): void {
    const permissionData =
      this._localStorageService.getItem<UserProfileViewModel>(
        USER_PERMISSIONS_STORAGE_KEY
      );
    if (
      CommonUtils.toJSON(this.currentPermissions) !==
      CommonUtils.toJSON(permissionData)
    ) {
      this._permissions$.next(permissionData);
    }
  }
}
